<template>
  <div class="modalBooking" id="new-modal-booking">
    <div class="modalBooking-bg" @click="close"></div>
    <div class="modalBooking-dialog">
      <div class="modalBooking-content">
        <div class="modalBooking-header">
          <h5 class="mb-0">{{ category.name }}</h5>
          <button class="modalBooking-exit" type="button" @click="close">
            <icon-base>
              <IconClose />
            </icon-base>
          </button>
        </div>
        <template v-if="showModal === 1">
          <div class="modalBooking-body" ref="modal-body">
            <ProductDetails
              :item="data.item"
              :productId="data.productId"
              :order="order"
              :orderIndex="orderIndex"
              @next="submit"
            />
          </div>
          <div class="modalBooking-footer">
            <div class="more-details" ref="more-details">
              <div class="toggler text-center">
                <button
                  class="btn btn-link"
                  type="button"
                  @click="handleShowDetails"
                  :style="{ color: primary }"
                >
                  {{ showDetails ? "Hide" : "Show" }} Details and Inclusions
                </button>
              </div>

              <div class="more-details--content">
                <div
                  class="product-feat"
                  :style="{
                    background: `linear-gradient(135deg, ${event} 0%,${primary} 50%,${secondary} 100%)`
                  }"
                  v-if="data.item.image"
                >
                  <img :src="data.item.image" :alt="data.item.name" />
                </div>
                <p>
                  <span class="fw-medium">Details:</span>
                </p>
                <p>{{ data.item.description }}</p>
                <template v-if="!!data.item.Inclusions.length">
                  <p>
                    <span class="fw-medium">Inclusions:</span>
                  </p>
                  <ul class="inclusions">
                    <li
                      v-for="item in data.item.Inclusions"
                      :key="`inclusion-${item.id}`"
                    >
                      {{ item.name }}
                    </li>
                  </ul>
                </template>
              </div>
            </div>
          </div>
        </template>
        <div class="modalBooking-body" v-if="showModal === 2">
          <div class="text-center mt-30">
            <img src="@/assets/media/space.svg" alt="" />
            <h4 class="mt-30 mb-10">No Item Found</h4>
            <p class="font-size-md">
              Sorry, no coworking space is available for this location.
            </p>
            <ActionButton
              :theme="{ primary, secondary, event, danger }"
              label="Back to Booking"
              @customEvent="close"
              class="w-auto my-0 mx-auto"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ActionButton from "@/components/ActionButton";
import ProductDetails from "@/components/booking/ProductDetails";
import IconClose from "@/components/icons/IconClose";
import query from "@/api/query";
import axios from "@/helpers/axios";

export default {
  name: "ModalBooking",
  components: {
    ProductDetails,
    ActionButton,
    IconClose
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    order: {
      type: Object,
      default: null
    },
    orderIndex: {
      type: [Number, String],
      default: null
    }
  },
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    },
    moreDetailsHeight() {
      return this.$refs["more-details"].scrollHeight;
    },
    category() {
      return this.data.item.Category;
    }
  },
  data() {
    return {
      productId: null,
      showModal: 0,
      showDetails: false
    };
  },
  mounted() {
    this.productId = this.data.productId;
    if (this.order) {
      this.showModal = 1;
    } else {
      this.handleSpaceAvailability();
    }
  },
  methods: {
    handleStatus() {
      const status = this.$route.matched[0].name;
      switch (status) {
        case "user":
          return "&status=2";
        case "concierge":
        case "admin":
          return "&status=1";
      }
      return "";
    },
    async handleSpaceAvailability() {
      const status = this.handleStatus();
      const count = await axios
        .get(
          `/categories/${this.data.categoryId}/availableProducts?${status}&locationId=${this.data.locationId}`
        )
        .then(response => response.data.count);
      this.showModal = count > 0 ? 1 : 2;
    },
    close() {
      this.$emit("close");
    },
    async submit({ orderPackage, term }) {
      try {
        const product = await query
          .getSingleData({
            model: "products",
            id: this.productId
          })
          .then(response => response.data);
        if (product.data) {
          const schedule = {
            Product: product.data,
            Term: term,
            OrderPackage: orderPackage
          };
          this.$emit("submit", schedule);
        }
      } catch (error) {
        this.$toastr.error(
          "Something went wrong! Please contact administrator",
          "Oops!"
        );
      }
    },
    handleChooseProduct({ productId }) {
      this.progress = "productDetails";
      this.productId = productId;
    },
    handleShowDetails() {
      this.showDetails = !this.showDetails;
      this.$refs["more-details"].classList.toggle("toggled");
      this.$refs["more-details"].style.transform = this.showDetails
        ? `translateY(-${this.$refs["more-details"].scrollHeight}px)`
        : "translateY(-80px)";
    }
  }
};
</script>

<style lang="scss" scoped>
.modalBooking {
  bottom: 0;
  left: 0;
  overflow: hidden auto;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;

  &-bg {
    background: black;
    bottom: 0;
    left: 0;
    opacity: 0.5;
    position: fixed;
    right: 0;
    top: 0;
  }

  &-dialog {
    animation: fade-in-up 0.25s ease-out;
    bottom: 0;
    display: flex;
    flex-flow: column;
    left: 0;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    width: 57.5rem;

    @media (min-width: $screen-sm) {
      max-width: calc(100% - 2rem);
      top: 1rem;
    }

    @media (min-width: $screen-md) {
      top: 3rem;
    }
  }

  &-content {
    background-clip: padding-box;
    background-color: #fff;
    display: flex;
    flex-flow: column;
    height: 640px;
    max-height: 640px;
    width: 100%;
    pointer-events: auto;

    @media (min-width: $screen-sm) {
      border-radius: 5px;
    }
  }

  &-header {
    align-items: flex-start;
    display: flex;
    padding: 2.5rem 2.5rem 1.5rem;
    position: relative;

    @media (min-width: $screen-lg) {
      padding-left: 2rem;
      padding-right: 2rem;
    }
    &-options {
      flex: 0 1 auto;
      margin-left: auto;
    }
  }

  &-body {
    min-height: calc(100% - 150px);
    overflow: auto;
    padding: 0.5rem 2.5rem 2rem;

    @media (min-width: $screen-sm) {
      height: auto;
      overflow: visible;
    }

    @media (min-width: $screen-lg) {
      padding-left: 2rem;
      padding-right: 2rem;
    }
  }

  &-alert {
    background-color: white;
    height: 400px;
    margin: calc(50vh - 200px) 0;

    @media (min-width: $screen-sm) {
      height: 280px;
      width: 500px;
      margin: calc(50% - 140px) calc(50% - 250px) !important;
    }

    @media (min-width: $screen-md) {
      width: 680px;
      margin: calc(50vh - 250px) calc(50% - 340px) !important;
    }
  }

  &-exit {
    background-color: transparent;
    border: 0;
    border-radius: 50%;
    cursor: pointer;
    font-size: 0.625rem;
    padding: 0.25rem;
    position: absolute;
    right: 1rem;
    top: 1.5rem;
    color: #4c4c4c;

    &:hover {
      opacity: 1;
    }
  }
}

// TODO: Extract more details to separate offcanvas style
.more-details {
  background-color: $gray-100;
  border-top-left-radius: 1.25rem;
  border-top-right-radius: 1.25rem;
  left: 0;
  max-height: 75vh;
  position: fixed;
  top: 100%;
  transform: translateY(-80px);
  transition: transform 250ms ease;
  width: 100%;
  z-index: 1000;

  @media (min-width: $screen-sm) {
    display: none;
  }

  .toggler {
    padding: 1.125rem 1rem;
  }

  &--content {
    max-height: calc(75vh - 80px);
    overflow: auto;
    padding: 0.0625rem 2.5rem 1.125rem;
  }

  ul {
    padding-left: 1.125rem;
  }
}

.toggled {
  transform: translateY(-75vh);
}

.product {
  &-feat {
    width: 100%;
    height: 8.75rem;
    margin-bottom: 1rem;
    overflow: hidden;
    border-radius: 5px;
    img {
      height: inherit;
      width: 100%;
      object-fit: cover;
    }
  }
}

@keyframes fade-in-up {
  0% {
    opacity: 0;
    transform: translateY(5vh);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
